/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { ChangeEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, ModalBody, ModalHeader, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { Loader } from '@g17eco/atoms/loader';
import { FileDropZone } from '@features/files/FileDropZone';
import G17Client, { formDataConfig } from '@services/G17Client';
import { reloadInitiative } from '@actions/initiative';
import { SubmitButton } from '@g17eco/molecules/button/SubmitButton';
import { RootState, useAppDispatch } from '@reducers/index';
import { withProfile } from '@utils/files';
import { BasicAlert } from '@g17eco/molecules/alert';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { documentOptions } from '@features/document-library/utils';
import { AiProcessingValue } from '@g17eco/types/ai';
import { Toggle } from '@g17eco/molecules/toggle';
import { allowAIProcessing } from '@utils/ai';

interface UploadMediaAndDocumentModalParams {
  toggle: (e: any) => void;
  isOpen: boolean | undefined;
}

interface FormState {
  title: string;
  ownerSubType?: string;
  files: any[];
  aiProcessing: AiProcessingValue;
}

const renderFile = function (file: any) {
  const isImage = 'type' in file && typeof file.type === 'string' && file.type.startsWith('image');
  return (
    <div>
      {isImage && <img src={file.preview} alt={file.name} className='imgUpload' />}
      <p className='fileUpload'>{file.name}</p>
    </div>
  );
};

const UploadMediaAndDocumentModal = (props: UploadMediaAndDocumentModalParams) => {
  const [form, setForm] = useState<FormState>({
    title: '',
    files: [],
    aiProcessing: AiProcessingValue.Include,
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [fileError, setFileError] = useState('');

  const initiativeId = useSelector((state: RootState) => state.initiative.data?._id);
  const dispatch = useAppDispatch();

  const updateForm = (e: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value.trim() });
  };

  const addFiles = (files: File[]) => {
    setForm({
      ...form,
      files: files.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })),
    });
    setFileError('');
  };

  const handleToggleAiProcessing = () => {
    setForm({
      ...form,
      aiProcessing: form.aiProcessing === AiProcessingValue.Include ? AiProcessingValue.Exclude : AiProcessingValue.Include,
    });
  };

  const handleSubmit = async (_: React.MouseEvent<any>) => {
    if (form.files.length === 0) {
      setFileError('There are no files to be uploaded');
      return;
    }
    setSaving(true);
    const payload = {
      ...withProfile(form),
      aiProcessing: [form.aiProcessing],
    };
    return G17Client.post(`initiatives/${initiativeId}/documents`, payload, formDataConfig)
      .then(() => {
        setSaving(false);
        dispatch(reloadInitiative());
        props.toggle(false);
      })
      .catch((e) => {
        setSaving(false);
        setMessage(e.message);
      });
  };

  return (
    <Modal isOpen={props.isOpen} toggle={props.toggle} backdrop='static' className='upload-media-documents-modal'>
      <ModalHeader toggle={props.toggle}>Upload Documents</ModalHeader>
      <ModalBody>
        {saving && <Loader />}
        {message && <div className='alert alert-danger'>{message}</div>}
        <Form>
          <FormGroup>
            <FileDropZone multiple={false} onDrop={addFiles} />
            <BasicAlert type={'danger'}>{fileError}</BasicAlert>
            <div className='mt-3'>
              {form.files.map((file) => (
                <span key={file.name}>{renderFile(file)}</span>
              ))}
            </div>
          </FormGroup>
          <FormGroup>
            <Label className='strong' for='title'>
              Title
            </Label>
            <Input id='title' type='text' name='title' onChange={updateForm} required />
          </FormGroup>
          <FormGroup>
            <Label className='strong' for='description'>
              Select document type (optional)
            </Label>
            <SelectFactory
              className='w-100 border-none'
              selectType={SelectTypes.SingleSelect}
              placeholder='Select a value'
              options={documentOptions}
              value={documentOptions.find(({ value }) => value === form.ownerSubType)}
              onChange={(op) => {
                setForm({ ...form, ownerSubType: op?.value });
              }}
              isSearchable={false}
              menuPlacement='bottom'
              isMenuPortalTargetBody
            />
          </FormGroup>
          {form.files.length > 0 && (
            <FormGroup>
              <div className='d-flex align-items-center gap-2'>
                <Toggle
                  checked={allowAIProcessing({ aiProcessing: form.aiProcessing })}
                  onChange={handleToggleAiProcessing}
                  testId='ai-processing-toggle'
                  label='AI Processing'
                />
              </div>
            </FormGroup>
          )}
          <div className='mt-4 text-right'>
            <Button color='link-secondary' className='me-3' onClick={props.toggle}>
              Cancel
            </Button>
            <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default UploadMediaAndDocumentModal;
